import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  CalendarDays,
  Camera,
  LogOut,
  Mail,
  User,
} from "lucide-react";
import {
  extractTeacherRecord,
  getTeacherDisplayName,
  getTeacherEmail,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSession,
  updateTeacherProfile,
} from "../../features/teacher/teacherSlice";

const profileStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

  .teacher-profile-page {
    min-height: 100vh;
    background:
      radial-gradient(circle at top left, rgba(255, 201, 164, 0.42), transparent 28%),
      radial-gradient(circle at top right, rgba(133, 169, 255, 0.28), transparent 24%),
      linear-gradient(180deg, #fffaf2 0%, #f7f1e7 100%);
    color: #1f1a17;
    font-family: "Lato", sans-serif;
    font-size: 1rem;
  }

  .teacher-profile-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid #ebe4d8;
    backdrop-filter: blur(14px);
  }

  .teacher-profile-shell {
    width: min(100%, 78rem);
    margin: 0 auto;
    padding-inline: clamp(0.875rem, 2vw, 1.25rem);
  }

  .teacher-profile-topbar-row {
    min-height: clamp(4rem, 5.5vw, 4.5rem);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: clamp(1rem, 2vw, 1.5rem);
  }

  .teacher-profile-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-profile-brand-badge {
    width: clamp(2.25rem, 2.8vw, 2.45rem);
    height: clamp(2.25rem, 2.8vw, 2.45rem);
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    color: #ffffff;
    box-shadow: 0 12px 20px rgba(255, 98, 77, 0.2);
  }

  .teacher-profile-brand-title {
    margin: 0;
    color: #ff624d;
    font-size: clamp(1.1rem, 1.7vw, 1.35rem);
    font-weight: 700;
  }

  .teacher-profile-actions {
    display: flex;
    align-items: center;
    gap: clamp(0.75rem, 1.5vw, 1rem);
  }

  .teacher-profile-email-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 2.5rem;
    padding-inline: 1rem;
    border: 1px solid #dbe3f2;
    border-radius: 999px;
    background: #ffffff;
    color: #6f6a67;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .teacher-profile-logout {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    border: 0;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
  }

  .teacher-profile-tabs {
    display: flex;
    align-items: center;
    gap: clamp(0.625rem, 1.5vw, 0.95rem);
    min-height: 3.35rem;
  }

  .teacher-profile-tab {
    min-height: 3.35rem;
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    padding-inline: 0.95rem;
    border: 0;
    border-bottom: 0.1875rem solid transparent;
    background: transparent;
    color: #2f2a28;
    font: inherit;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .teacher-profile-tab-active {
    color: #2453d4;
    border-bottom-color: #2453d4;
    background: rgba(226, 235, 255, 0.78);
    border-top-left-radius: 0.875rem;
    border-top-right-radius: 0.875rem;
  }

  .teacher-profile-content {
    padding: clamp(1.25rem, 2.5vw, 1.65rem) clamp(0.875rem, 1.6vw, 1.25rem)
      clamp(2rem, 4vw, 2.75rem);
  }

  .teacher-profile-hero {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(248, 177, 132, 0.96);
    border-radius: 1.5rem;
    background: linear-gradient(135deg, #fff1e6 0%, #ffbe7a 42%, #ff7c5c 100%);
    padding: clamp(1.35rem, 2.4vw, 2rem);
    margin-bottom: 1rem;
    box-shadow: 0 22px 40px rgba(195, 102, 58, 0.18);
    color: #5a2618;
  }

  .teacher-profile-hero::after {
    content: "";
    position: absolute;
    inset: auto -8% -22% auto;
    width: 14rem;
    height: 14rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.36);
  }

  .teacher-profile-hero-grid {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(16rem, 0.9fr);
    gap: 1rem;
    align-items: end;
  }

  .teacher-profile-hero-copy span {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 2rem;
    padding: 0.3rem 0.8rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.72);
    color: #b34722;
    font-size: 0.83rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .teacher-profile-hero-copy h3 {
    margin: 0.9rem 0 0.55rem;
    font-size: clamp(1.8rem, 3vw, 2.45rem);
    line-height: 1.08;
    font-weight: 900;
  }

  .teacher-profile-hero-copy p {
    margin: 0;
    max-width: 38rem;
    color: #7f4a34;
    font-size: 0.98rem;
    line-height: 1.6;
  }

  .teacher-profile-hero-stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .teacher-profile-hero-stat {
    border: 1px solid rgba(255, 204, 167, 0.98);
    border-radius: 1rem;
    background: rgba(255, 245, 238, 0.9);
    backdrop-filter: blur(10px);
    padding: 0.95rem 1rem;
    box-shadow: 0 12px 20px rgba(199, 107, 68, 0.12);
  }

  .teacher-profile-hero-stat strong {
    display: block;
    margin-top: 0.35rem;
    font-size: 1.15rem;
    font-weight: 800;
  }

  .teacher-profile-hero-stat span {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #cb6431;
  }

  .teacher-profile-banner-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .teacher-profile-banner-copy {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .teacher-profile-update-button {
    min-height: 3.1rem;
    padding: 0.8rem 1.2rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    background: linear-gradient(135deg, #ff7f66 0%, #f25b4b 100%);
    box-shadow: 0 14px 24px rgba(242, 91, 75, 0.24);
    color: #ffffff;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 800;
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
  }

  .teacher-profile-update-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 28px rgba(242, 91, 75, 0.28);
  }

  .teacher-profile-banner {
    border: 1px solid rgba(215, 227, 251, 0.92);
    border-radius: 1.15rem;
    background: rgba(255, 255, 255, 0.78);
    backdrop-filter: blur(16px);
    padding: clamp(1.25rem, 2vw, 1.6rem);
    margin-bottom: 1rem;
    box-shadow: 0 18px 34px rgba(73, 96, 140, 0.08);
  }

  .teacher-profile-banner h2 {
    margin: 0 0 0.75rem;
    font-size: clamp(1.15rem, 1.8vw, 1.35rem);
    font-weight: 800;
  }

  .teacher-profile-banner p {
    margin: 0;
    color: #6c6764;
    font-size: 0.96rem;
    line-height: 1.6;
  }

  .teacher-profile-status {
    border-radius: 0.9rem;
    border: 1px solid transparent;
    padding: 0.95rem 1rem;
    margin-bottom: 1rem;
    font-size: 0.92rem;
  }

  .teacher-profile-status-error {
    border-color: #ffd1c7;
    background: #fff3f0;
    color: #b34736;
  }

  .teacher-profile-status-info {
    border-color: #d7e3fb;
    background: #eef4ff;
    color: #315db7;
  }

  .teacher-profile-card {
    border: 1px solid rgba(221, 229, 239, 0.95);
    border-radius: 1.5rem;
    background: rgba(255, 255, 255, 0.94);
    padding: clamp(1rem, 1.8vw, 1.5rem);
    box-shadow: 0 26px 40px rgba(71, 85, 110, 0.09);
  }

  .teacher-profile-card-grid {
    display: grid;
    grid-template-columns: minmax(18rem, 22rem) minmax(0, 1fr);
    gap: 1.35rem;
    align-items: start;
  }

  .teacher-profile-summary {
    border-radius: 1.25rem;
    background: linear-gradient(180deg, #fff7ee 0%, #fff 100%);
    border: 1px solid #f2dfca;
    padding: 1.2rem;
  }

  .teacher-profile-summary-name {
    margin: 1rem 0 0.35rem;
    font-size: 1.45rem;
    font-weight: 800;
  }

  .teacher-profile-summary-email {
    margin: 0;
    color: #6e6864;
    line-height: 1.55;
  }

  .teacher-profile-badge-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.7rem;
    margin-top: 1rem;
  }

  .teacher-profile-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 2.35rem;
    padding: 0.45rem 0.85rem;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid #e9dfd2;
    color: #413933;
    font-size: 0.88rem;
    font-weight: 700;
  }

  .teacher-profile-data-panel {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .teacher-profile-photo-title {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    color: #1f1a17;
    font-size: clamp(0.95rem, 1.3vw, 1.04rem);
    font-weight: 500;
  }

  .teacher-profile-photo-title svg {
    color: #2453d4;
  }

  .teacher-profile-photo-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
    margin-bottom: 0;
  }

  .teacher-profile-photo-box {
    width: 7.75rem;
    height: 7.75rem;
    border-radius: 1.4rem;
    background: linear-gradient(135deg, #1e2430 0%, #444e61 100%);
    box-shadow: 0 18px 26px rgba(0, 0, 0, 0.18);
    flex: 0 0 auto;
    overflow: hidden;
    display: grid;
    place-items: center;
    color: #ffffff;
    font-size: 1.5rem;
    font-weight: 700;
  }

  .teacher-profile-photo-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .teacher-profile-photo-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .teacher-profile-upload-button {
    min-height: 2.85rem;
    padding-inline: 1.4rem;
    border: 0;
    border-radius: 0.75rem;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 14px 22px rgba(255, 98, 77, 0.2);
    color: #ffffff;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
  }

  .teacher-profile-upload-button:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  .teacher-profile-upload-note {
    color: #7a7673;
    font-size: 0.9rem;
  }

  .teacher-profile-field-stack {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .teacher-profile-field {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  .teacher-profile-field label,
  .teacher-profile-group-label {
    color: #1f1a17;
    font-size: 0.95rem;
    font-weight: 500;
  }

  .teacher-profile-input,
  .teacher-profile-textarea {
    width: 100%;
    min-height: 3rem;
    border: 1px solid #e3e8ef;
    border-radius: 1rem;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    color: #1f1a17;
    font: inherit;
    font-size: 0.95rem;
    padding: 0.9rem 1rem;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  .teacher-profile-textarea {
    min-height: 3.1rem;
    padding-top: 0.9rem;
    padding-bottom: 0.9rem;
    resize: vertical;
  }

  .teacher-profile-chip-section {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .teacher-profile-chip-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .teacher-profile-chip-grid-specializations {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .teacher-profile-chip {
    min-height: 3rem;
    border: 1px solid #dde5ef;
    border-radius: 0.75rem;
    background: #ffffff;
    color: #1f1a17;
    font: inherit;
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
  }

  .teacher-profile-chip:hover {
    border-color: #c9d8fb;
  }

  .teacher-profile-chip-active {
    border-color: #2453d4;
    background: #f5f8ff;
    color: #2453d4;
  }

  .teacher-profile-group-note {
    margin: 0;
    color: #7a7673;
    font-size: 0.9rem;
  }

  .teacher-profile-save {
    width: 100%;
    min-height: 3.4rem;
    margin-top: 1rem;
    border: 0;
    border-radius: 0.85rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 16px 26px rgba(255, 98, 77, 0.2);
    color: #ffffff;
    font: inherit;
    font-size: 0.98rem;
    font-weight: 700;
    cursor: pointer;
  }

  .teacher-profile-save:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  .teacher-profile-hidden-input {
    display: none;
  }

  @media (max-width: 900px) {
    .teacher-profile-hero-grid,
    .teacher-profile-card-grid {
      grid-template-columns: 1fr;
    }

    .teacher-profile-chip-grid,
    .teacher-profile-chip-grid-specializations {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 768px) {
    .teacher-profile-topbar-row {
      min-height: auto;
      padding: 1.125rem 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-profile-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .teacher-profile-email-pill,
    .teacher-profile-logout {
      justify-content: center;
    }

    .teacher-profile-tabs {
      overflow-x: auto;
      padding-bottom: 0.5rem;
    }

    .teacher-profile-tab {
      white-space: nowrap;
    }

    .teacher-profile-photo-row {
      align-items: flex-start;
      flex-direction: column;
    }

    .teacher-profile-banner-row {
      align-items: flex-start;
      flex-direction: column;
    }

    .teacher-profile-update-button {
      width: 100%;
    }

    .teacher-profile-field-stack,
    .teacher-profile-hero-stats {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 540px) {
    .teacher-profile-chip-grid,
    .teacher-profile-chip-grid-specializations {
      grid-template-columns: 1fr;
    }
  }
`;



const toTrimmedString = (value) =>
  value === null || typeof value === "undefined" ? "" : String(value).trim();

const getAvatarLetter = (value = "") =>
  String(value).trim().charAt(0).toUpperCase() || "T";





export default function TeacherProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const teacherSession = useSelector((state) => state.teacher?.teacher);
  const profileQuery = useQuery({
    queryKey: ["teacher-profile-page"],
    queryFn: teacherApi.getProfile,
    retry: false,
  });

  useEffect(() => {
    const teacherRecord = extractTeacherRecord(profileQuery.data);
    if (teacherRecord) {
      dispatch(updateTeacherProfile(teacherRecord));
    }
  }, [dispatch, profileQuery.data]);

  const teacherProfile = useMemo(
    () => extractTeacherRecord(profileQuery.data, teacherSession || {}) || teacherSession || {},
    [profileQuery.data, teacherSession]
  );
  const teacherName = toTrimmedString(profileQuery.data?.data?.name || teacherProfile?.name || getTeacherDisplayName(teacherProfile)) || 'N/A';
  const teacherEmail = toTrimmedString(profileQuery.data?.data?.email || teacherProfile?.email || getTeacherEmail(teacherProfile)) || 'N/A';
  const teacherPhone = toTrimmedString(profileQuery.data?.data?.phone || teacherProfile?.phone) || 'N/A';
  const teacherExperience = toTrimmedString(profileQuery.data?.data?.experience || teacherProfile?.experience) || 'N/A';
  const teacherRating = toTrimmedString(profileQuery.data?.data?.rating || teacherProfile?.rating) || 'N/A';
  const teacherYear = toTrimmedString(profileQuery.data?.data?.year || teacherProfile?.year);
  const teacherYearDisplay = teacherYear ? `Year ${teacherYear}` : 'N/A';
  const isVerified = (profileQuery.data?.data?.isVerified || teacherProfile?.isVerified) === "1";

  const handleLogout = () => {
    dispatch(clearTeacherSession());
    navigate("/teacherlogin", { replace: true });
  };

  const handleUpdateProfile = () => {
    navigate("/teacherprofile/update");
  };

  return (
    <>
      <style>{profileStyles}</style>
      <main className="teacher-profile-page">
        <header className="teacher-profile-topbar">
          <div className="teacher-profile-shell">
            <div className="teacher-profile-topbar-row">
              <div className="teacher-profile-brand">
                <div className="teacher-profile-brand-badge" aria-hidden="true">
                  <BookOpen size={20} />
                </div>
                <h1 className="teacher-profile-brand-title">Teacher Dashboard</h1>
              </div>

              <div className="teacher-profile-actions">
                <div className="teacher-profile-email-pill">
                  <Mail size={18} />
                  {teacherEmail || "Teacher account"}
                </div>
                <button
                  className="teacher-profile-logout"
                  type="button"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="teacher-profile-shell">
          <nav className="teacher-profile-tabs" aria-label="Teacher tabs">
            <button
              className="teacher-profile-tab"
              type="button"
              onClick={() => navigate("/teacherdashboard")}
            >
              <BookOpen size={20} />
              Bookings
            </button>
            <button
              className="teacher-profile-tab"
              type="button"
              onClick={() => navigate("/teacheravailability")}
            >
              <CalendarDays size={20} />
              Availability
            </button>
            <button
              className="teacher-profile-tab teacher-profile-tab-active"
              type="button"
            >
              <User size={20} />
              Profile
            </button>
          </nav>
        </div>

        <div className="teacher-profile-shell teacher-profile-content">
          <section className="teacher-profile-hero">
            <div className="teacher-profile-hero-grid">
              <div className="teacher-profile-hero-copy">
                <span>
                  <User size={14} />
                  Teacher Profile
                </span>
                <h3>{teacherName}</h3>
                <p>
                  A cleaner profile view for your teaching identity, contact details,
                  and academic information.
                </p>
              </div>

              <div className="teacher-profile-hero-stats" aria-label="Teacher profile highlights">
                <div className="teacher-profile-hero-stat">
                  <span>Experience</span>
                  <strong>{teacherExperience}</strong>
                </div>
                <div className="teacher-profile-hero-stat">
                  <span>Year</span>
                  <strong>{teacherYearDisplay}</strong>
                </div>
                <div className="teacher-profile-hero-stat">
                  <span>Rating</span>
                  <strong>{teacherRating}</strong>
                </div>
                <div className="teacher-profile-hero-stat">
                  <span>Status</span>
                  <strong>{isVerified ? "Verified" : "Active"}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="teacher-profile-banner">
            <div className="teacher-profile-banner-row">
              <div className="teacher-profile-banner-copy">
                <h2>Profile Details</h2>
                <p>
                  View your profile information.
                </p>
              </div>

              <button
                className="teacher-profile-update-button"
                type="button"
                onClick={handleUpdateProfile}
              >
                Update Profile
              </button>
            </div>
          </section>


          {profileQuery.isLoading && !teacherSession ? (
            <div className="teacher-profile-status teacher-profile-status-info">
              Loading teacher profile...
            </div>
          ) : null}

          {profileQuery.isError ? (
            <div className="teacher-profile-status teacher-profile-status-error">
              {profileQuery.error.message}
            </div>
          ) : null}

          <section className="teacher-profile-card">
            <div className="teacher-profile-card-grid">
              <aside className="teacher-profile-summary">
                <div className="teacher-profile-photo-title">
                  <Camera size={18} />
                  Profile Photo
                </div>

                <div className="teacher-profile-photo-row">
                  <div className="teacher-profile-photo-box" aria-hidden="true">
                    {getAvatarLetter(teacherName)}
                  </div>
                </div>

                <h3 className="teacher-profile-summary-name">{teacherName}</h3>
                <p className="teacher-profile-summary-email">{teacherEmail}</p>

                <div className="teacher-profile-badge-row">
                  <span className="teacher-profile-badge">
                    <CalendarDays size={16} />
                    {teacherYearDisplay}
                  </span>
                  <span className="teacher-profile-badge">
                    <BookOpen size={16} />
                    {teacherExperience}
                  </span>
                  <span className="teacher-profile-badge">
                    <Mail size={16} />
                    {isVerified ? "Verified" : "Unverified"}
                  </span>
                </div>
              </aside>

              <div className="teacher-profile-data-panel">
                <div className="teacher-profile-field-stack">
                  <div className="teacher-profile-field">
                    <label htmlFor="teacher-full-name">Full Name</label>
                    <div className="teacher-profile-input" id="teacher-full-name">{teacherName}</div>
                  </div>

                  <div className="teacher-profile-field">
                    <label htmlFor="teacher-email">Email</label>
                    <div className="teacher-profile-input" id="teacher-email">{teacherEmail}</div>
                  </div>

                  <div className="teacher-profile-field">
                    <label htmlFor="teacher-phone">Phone</label>
                    <div className="teacher-profile-input" id="teacher-phone">{teacherPhone}</div>
                  </div>

                  <div className="teacher-profile-field">
                    <label>Years of Experience</label>
                    <div className="teacher-profile-input">{teacherExperience}</div>
                  </div>

                  <div className="teacher-profile-field">
                    <label>Year</label>
                    <div className="teacher-profile-input">{teacherYearDisplay}</div>
                  </div>

                  <div className="teacher-profile-field">
                    <label>Rating</label>
                    <div className="teacher-profile-input">{teacherRating}</div>
                  </div>
                </div>

                {isVerified && (
                  <div className="teacher-profile-status teacher-profile-status-info">
                    Verified
                  </div>
                )}
              </div>
            </div>
          </section>

        </div>
      </main>
    </>
  );
}
